<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '@/modules/commissioning/stores/deviceSpecificationStore';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import {
  getTerminalGroupingWithErrorsFromCommissionResult,
  getCommissionResultConfigValues,
} from '@/modules/commissioning/utils/error-resolver';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ctReadingIncorrect_img_01 from '@/modules/commissioning/assets/troubleshooting/CTReadingIncorrect_img_01.png';
import ctReadingIncorrect_img_02 from '@/modules/commissioning/assets/troubleshooting/CTReadingIncorrect_img_02.png';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'CTMissing' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);
const connections = deviceSpecificationStore.solshareSystemSpec.connections;

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  //ERROR_CTA_BUTTONS.RECONFIGURE,
  ERROR_CTA_BUTTONS.TRY_AGAIN,
];

/**
 * Get the terminal error grouping from the commission result
 */
const terminalErrorGrouping = getTerminalGroupingWithErrorsFromCommissionResult(
  commissionResult,
  connections,
);
const CTMissingUnits = terminalErrorGrouping[errorType] ?? [];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">No test current</div></template>
    <template #subTitle>
      <div class="error-sub-title">
        SolShare was unable to read the commissioning test current on the grid CT.
      </div>
    </template>

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>This usually indicates that the CT is not connected, incorrectly installed, or positioned in the wrong orientation.</p>
      <p>
        <strong>Each CT must be</strong>
      </p>
      <ul class="error-bullet-list">
        <li>Clipped around the correct cable at the meter</li>
        <li>Clipped in the correct orientation at the meter</li>
        <li>Correctly fitted to the SolShare connector</li>
        <li>Match the current rating selected during setup</li>
        <li>If extended CT, good electrical contact at intermediate connection</li>
      </ul>
      <p>
        You need to <strong>check both ends</strong> of the connection and any
        <strong>CT extensions</strong> that may be used in the installation.
      </p>
      <div class="error-intro-image">
        <img
          :src="ctReadingIncorrect_img_01"
          alt="Diagram: CT clamp on the meter supply, arrow toward load, split to tenancy and SolShare"
        />
      </div>
    </div>

    <div class="error-handle-section">
      <h4>CT Install End</h4>
      <p>Check that the CT is:</p>
      <ul class="error-bullet-list">
        <li>Clipped around the correct cable for that connection</li>
        <li>Clipped on the service side of the solar connection</li>
      </ul>
      <p>For connections listed below check the CT.</p>
      <p>Note the arrow markings on the base of the clip CT.</p>
      <div class="error-intro-image">
        <img
          :src="ctReadingIncorrect_img_02"
          alt="Diagram: Note the arrow markings on the base of the clip CT."
        />
      </div>
      <div v-if="CTMissingUnits.length > 0">
        <p>Connection to check:</p>
        <ul>
          <li v-for="(unit, i) in CTMissingUnits" :key="i">
            {{ unit }}
          </li>
        </ul>
      </div>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
